import { useEffect, useState } from "react";
import { getRandomCardImages } from "./images";

export type CardType = {
  id: number;
  value: number;
  image: string;
  matched: boolean;
};

function shuffle<T>(arr: T[]): T[] {
  return [...arr]
    .map((a) => ({ sort: Math.random(), value: a }))
    .sort((a, b) => a.sort - b.sort)
    .map((a) => a.value);
}

type CompleteStats = { time: number; moves: number };

export function useMemoryGame(onComplete: (stats: CompleteStats) => void) {
  const [cards, setCards] = useState<CardType[]>([]);
  const [first, setFirst] = useState<number | null>(null);
  const [second, setSecond] = useState<number | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [moves, setMoves] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [completed, setCompleted] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  // init game
  useEffect(() => {
    initGame();
  }, []);

  // update elapsed time every second
  useEffect(() => {
    if (!startTime || completed) return;
    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime, completed]);

  const initGame = () => {
    const cardImages = getRandomCardImages();
    const duplicated = cardImages.flatMap((img, idx) => [
      { id: idx * 2, value: idx, image: img, matched: false },
      { id: idx * 2 + 1, value: idx, image: img, matched: false },
    ]);
    setCards(shuffle(duplicated));
    setFirst(null);
    setSecond(null);
    setDisabled(false);
    setMoves(0);
    setCompleted(false);
    setElapsedTime(0);
    // Timer sẽ bắt đầu khi người chơi lật thẻ đầu tiên
    setStartTime(null);
  };

  // khi tất cả matched → tính thời gian & gọi onComplete
  useEffect(() => {
    if (cards.length === 0 || completed) return;
    const allMatched = cards.every((c) => c.matched);
    if (allMatched) {
      const end = Date.now();
      const time = startTime ? (end - startTime) / 1000 : 0;
      setCompleted(true);
      onComplete({ time, moves });
    }
  }, [cards, completed, moves, startTime, onComplete]);

  const handleCardClick = (index: number) => {
    if (disabled || completed) return;
    if (index === first || index === second) return;

    if (first === null) {
      setFirst(index);
      // nếu vì lý do nào đó startTime chưa set, set tại lần lật đầu tiên
      if (!startTime) {
        setStartTime(Date.now());
      }
      return;
    }

    if (second === null) {
      setSecond(index);
      setMoves((m) => m + 1);
      setDisabled(true);

      const firstCard = cards[first];
      const secondCard = cards[index];

      if (firstCard.value === secondCard.value) {
        setTimeout(() => {
          setCards((prev) =>
            prev.map((c, i) =>
              i === first || i === index ? { ...c, matched: true } : c
            )
          );
          resetTurn();
        }, 350);
      } else {
        setTimeout(() => {
          resetTurn();
        }, 700);
      }
    }
  };

  const resetTurn = () => {
    setFirst(null);
    setSecond(null);
    setDisabled(false);
  };

  const resetGame = () => {
    initGame();
  };

  return {
    cards,
    first,
    second,
    moves,
    completed,
    elapsedTime,
    handleCardClick,
    resetGame,
  };
}